import React, { useEffect } from "react";
import {
  FaPaintBrush,
  FaMagic,
  FaCamera,
  FaLayerGroup,
  FaFont,
  FaPhotoVideo,
  FaPalette,
  FaBriefcase,
  FaFilm,
  FaLaptopCode,
  FaCameraRetro,
  FaBullhorn,
} from "react-icons/fa";
import AOS from "aos";
import "aos/dist/aos.css";
import "./Animation.css";
import { Link } from "react-router-dom";

// Images
const IMAGES = {
  hero: "/assets/Thumb-Photoshop-2.jpg",
  workflow:
    "/assets/adobe-photoshop-photo-editing.jpg",
};

const Adopep5 = () => {
  useEffect(() => {
    AOS.init({ duration: 700, once: true });
    window.scrollTo(0, 0);
  }, []);

  const features = [
    {
      icon: (
        <FaPaintBrush className="text-blue-500 text-3xl animate-bounce-slow" />
      ),
      title: "Advanced Brushes",
      desc: "New natural brushes for realistic painting and illustration.",
      color: "bg-blue-50",
    },
    {
      icon: <FaCamera className="text-pink-500 text-3xl animate-pulse-slow" />,
      title: "Image Enhancements",
      desc: "Retouch, sharpen, and color-correct photos like never before.",
      color: "bg-pink-50",
    },
    {
      icon: <FaLayerGroup className="text-green-500 text-3xl animate-wiggle" />,
      title: "Smarter Layers",
      desc: "Group, mask, and blend layers with non-destructive editing.",
      color: "bg-green-50",
    },
    {
      icon: <FaMagic className="text-purple-500 text-3xl animate-float" />,
      title: "Magic Effects",
      desc: "Apply AI-powered filters and stunning visual effects instantly.",
      color: "bg-purple-50",
    },
    {
      icon: <FaFont className="text-red-500 text-3xl animate-shake" />,
      title: "Typography Power",
      desc: "Design professional text effects with new font tools.",
      color: "bg-red-50",
    },
    {
      icon: (
        <FaPhotoVideo className="text-orange-500 text-3xl animate-bounce" />
      ),
      title: "Web & Multimedia",
      desc: "Export designs optimized for web, apps, and presentations.",
      color: "bg-orange-50",
    },
  ];

  const workflow = [
    "Import and organize raw images.",
    "Retouch photos with healing tools.",
    "Apply filters, effects, and layer adjustments.",
    "Add creative text and vector elements.",
    "Export for print, digital, and web formats.",
  ];

  const industries = [
    {
      icon: <FaPalette className="text-pink-500 text-3xl animate-float" />,
      title: "Graphic Design",
      text: "Create posters, flyers, and digital ads.",
    },
    {
      icon: (
        <FaCameraRetro className="text-blue-500 text-3xl animate-bounce-slow" />
      ),
      title: "Photography",
      text: "Professional photo retouching and enhancements.",
    },
    {
      icon: (
        <FaLaptopCode className="text-green-500 text-3xl animate-slideInRight" />
      ),
      title: "Web Design",
      text: "Craft stunning UI mockups and layouts.",
    },
    {
      icon: <FaBullhorn className="text-purple-500 text-3xl animate-wiggle" />,
      title: "Social Media",
      text: "Design branded content for Instagram & YouTube.",
    },
    {
      icon: <FaFilm className="text-red-500 text-3xl animate-shake" />,
      title: "Film & Media",
      text: "Produce posters, visuals, and movie assets.",
    },
    {
      icon: <FaBriefcase className="text-orange-500 text-3xl animate-bounce" />,
      title: "Corporate Branding",
      text: "Logos, presentations, and brand identity.",
    },
  ];

  const syllabus = [
    {
      module: "Module 1: Introduction",
      content: "Basics of Photoshop, UI navigation, and essential tools.",
    },
    {
      module: "Module 2: Layers & Masks",
      content:
        "Understanding layers, blending modes, and non-destructive editing.",
    },
    {
      module: "Module 3: Photo Editing",
      content: "Retouching, healing, color corrections, and adjustments.",
    },
    {
      module: "Module 4: Typography & Effects",
      content: "Creative text design, styles, and special effects.",
    },
    {
      module: "Module 5: Digital Projects",
      content: "Creating posters, social media ads, and branding assets.",
    },
    {
      module: "Module 6: Final Project",
      content: "Hands-on capstone project to showcase your skills.",
    },
  ];

  return (
    <div className="bg-white text-slate-900 min-h-screen overflow-hidden">
      {/* Hero */}
      <section
        className="relative flex flex-col items-center justify-center text-center py-32"
        style={{
          backgroundImage: `linear-gradient(to right, #2563eb, #9333ea), url(${IMAGES.hero})`,
          backgroundBlendMode: "overlay",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="relative z-10 px-6">
          <h1 className="text-5xl sm:text-6xl font-extrabold text-white drop-shadow-md animate-fadeIn">
            Adobe Photoshop 5
          </h1>
          <p className="mt-6 text-lg md:text-xl text-white">
            Explore the legendary version that revolutionized digital design,
            photography, and creative workflows.
          </p>
          {/* Enroll Button */}
          <div className="mt-8">
            <Link
              to="/contact"
              className="px-8 py-4 rounded-full bg-gradient-to-r from-orange-500 to-pink-500 text-white font-semibold shadow-lg hover:scale-110 transition transform duration-300 animate-bounce-slow"
            >
              🚀 Enroll Now
            </Link>
          </div>
        </div>
      </section>

      {/* Syllabus Section */}
      <section className="max-w-6xl mx-auto px-6 py-16">
        <h2 className="text-3xl font-bold text-center mb-12" data-aos="fade-up">
          Course Syllabus
        </h2>
        <div className="grid sm:grid-cols-2 gap-8 animate-bounce-slow">
          {syllabus.map((s, i) => (
            <div
              key={i}
              className="p-6 bg-slate-50 rounded-xl shadow hover:shadow-xl transition duration-300"
              data-aos="fade-up"
              data-aos-delay={i * 100}
            >
              <h3 className="font-semibold text-lg text-blue-600">
                {s.module}
              </h3>
              <p className="text-slate-600 text-sm mt-2">{s.content}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Features */}
      <section className="max-w-6xl mx-auto px-6 py-16">
        <h2 className="text-3xl font-bold text-center mb-12" data-aos="fade-up">
          Key Features in Photoshop 5
        </h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
          {features.map((f, i) => (
            <div
              key={i}
              className={`p-6 ${f.color} rounded-xl shadow hover:shadow-2xl hover:scale-105 transition transform duration-300`}
              data-aos="zoom-in"
              data-aos-delay={i * 100}
            >
              <div className="mb-4">{f.icon}</div>
              <h3 className="font-semibold text-lg">{f.title}</h3>
              <p className="text-slate-600 text-sm mt-2">{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Workflow */}
      <section className="bg-slate-50 py-16">
        <div className="max-w-6xl mx-auto px-6 grid md:grid-cols-2 gap-12 items-center">
          <div data-aos="fade-right">
            <img
              src={IMAGES.workflow}
              alt="Workflow"
              className="rounded-xl shadow-lg animate-float"
            />
          </div>
          <div data-aos="fade-left">
            <h3 className="text-2xl font-bold">Creative Workflow</h3>
            <ol className="mt-6 space-y-4 animate-float">
              {workflow.map((step, i) => (
                <li
                  key={i}
                  className="flex gap-4 items-start animate-slideInRight"
                  style={{ animationDelay: `${i * 0.3}s` }}
                >
                  <span className="flex items-center justify-center w-8 h-8 rounded-full bg-blue-500 text-white font-bold shadow">
                    {i + 1}
                  </span>
                  <p className="text-slate-700">{step}</p>
                </li>
              ))}
            </ol>
          </div>
        </div>
      </section>

      {/* Industry Usage */}
      <section className="max-w-6xl mx-auto px-6 py-16">
        <h3 className="text-3xl font-bold text-center mb-12" data-aos="fade-up">
          Where Photoshop 5 is Used
        </h3>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
          {industries.map((ind, i) => (
            <div
              key={i}
              className="p-6 rounded-xl bg-gradient-to-br from-slate-100 to-slate-200 shadow hover:scale-110 transition transform duration-300 text-center animate-hover-shake"
              data-aos="flip-left"
              data-aos-delay={i * 100}
            >
              <div className="mb-4 flex justify-center">{ind.icon}</div>
              <h4 className="font-semibold text-lg">{ind.title}</h4>
              <p className="text-slate-600 text-sm mt-2">{ind.text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Enroll Section */}
      <section
        id="enroll"
        className="py-20 text-center"
        style={{
          background: "linear-gradient(135deg, #ff6a00, #ee0979)", // 🔥 Contrast gradient
          color: "white",
        }}
      >
        <h2 className="text-3xl font-bold mb-6">
          Ready to Master Photoshop 5?
        </h2>
        <p className="max-w-2xl mx-auto mb-8 text-lg">
          Enroll now to gain hands-on experience, expert guidance, and a
          certificate of completion to showcase your skills.
        </p>
        <Link
          to="/contact"
          className="px-10 py-4 rounded-full bg-white text-pink-600 font-bold text-lg shadow-lg hover:scale-110 transition transform duration-300 animate-bounce-slow"
        >
          🚀 Enroll Now
        </Link>
      </section>
    </div>
  );
};

export default Adopep5;
